import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { ReactComponent as PlusIcon } from 'Assets/svg/plus-icon.svg';
import { ReactComponent as TripleDotNavIcon } from 'Assets/svg/triple-dot-nav.svg';
import { confirmationPopupState } from 'Components/common/confirmationPopup';
import ToolTipWithIcon from 'Components/common/ToolTipWithIcon';
import Dropdown from 'Components/common/dropdown';
import { LoadingWrapper } from 'Components/common/loadingWrapper';
import { userDetail } from 'Stores/userDetail';
import profileState from 'Stores/profile';
import { Utils } from 'Utils/utils';
import * as CONSTANTS from 'Utils/constants';
import UserDataPopup from './userDataPopup/userDataPopup';
import './manageTeam.scss';

const ManageTeam = observer(() => {
  const { noOfSeatsEligible, noOfSeatsUsed } = userDetail.userInfo;

  const [userDataPopup, setUserDataPopup] = useState({
    show: false,
    type: '',
    data: {},
  });

  useEffect(() => {
    profileState.getTeamDetails();
  }, []);

  const removeUser = member => {
    const { emailId, memberId } = member;

    confirmationPopupState.setPopupValues({
      customClassName: 'remove-user-popup',
      title: 'Remove User',
      content: `Are you sure you want to remove <span>${emailId}</span> from your team plan ?`,
      actionBtnText: 'Confirm',
      callback: () => {
        profileState.removeUser({ email: emailId, memberId });
        Utils.mixpanelTrack(CONSTANTS.MANAGE_TEAM_EVENTS.LB_DELETE_MEMBER, {
          memberEmail: emailId,
        });
      },
    });
    confirmationPopupState.setShowPopup(true);
  };

  const onDataSubmit = async data => {
    if (userDataPopup.type === 'ADD') {
      Utils.mixpanelTrack(CONSTANTS.MANAGE_TEAM_EVENTS.LB_ADD_MEMBER, {
        memberEmail: data.email,
        creditLimit: data.credits === -1 ? 'No Limit' : data.credits,
      });
      await profileState.addUser(data);
    } else {
      Utils.mixpanelTrack(CONSTANTS.MANAGE_TEAM_EVENTS.LB_EDIT_MEMBER, {
        memberEmail: data.email,
        creditLimit: data.credits === -1 ? 'No Limit' : data.credits,
      });
      await profileState.editUser({
        email: data.email,
        credits: data.credits,
        memberId: data.memberId,
      });
    }
    setUserDataPopup({
      show: false,
      type: '',
      data: {},
    });
  };

  const billingCycle =
    profileState.teamDetails.startDate && profileState.teamDetails.endDate
      ? `${Utils.getFormattedDate(
          profileState.teamDetails.startDate,
        )} - ${Utils.getFormattedDate(profileState.teamDetails.endDate)}`
      : '';

  const monthlyCycle =
    profileState.teamDetails.creditStartDate &&
    profileState.teamDetails.creditEndDate
      ? `${Utils.getFormattedDate(
          profileState.teamDetails.creditStartDate,
        )} - ${Utils.getFormattedDate(profileState.teamDetails.creditEndDate)}`
      : '';

  return (
    <div className="manage-team-page">
      <LoadingWrapper
        show={profileState.teamMemberApiLoading}
        position="fixed"
      />
      {userDataPopup.show && (
        <UserDataPopup
          title={userDataPopup.type === 'ADD' ? 'Add New User' : 'Edit User'}
          onDataSubmit={onDataSubmit}
          type={userDataPopup.type}
          data={userDataPopup.data}
          options={userDataPopup.options}
          onClose={() =>
            setUserDataPopup({
              show: false,
              type: '',
              data: {},
            })
          }
        />
      )}
      <div className="top-section">
        <div className="seats-and-billing">
          My Team
          <span className="seats-used">
            (
            <span>
              {noOfSeatsUsed}/{noOfSeatsEligible}
            </span>{' '}
            seats used)
          </span>
        </div>
        <button
          type="button"
          className="add-user"
          onClick={e => {
            if (
              noOfSeatsUsed &&
              noOfSeatsEligible &&
              noOfSeatsUsed < noOfSeatsEligible
            ) {
              setUserDataPopup({
                show: true,
                type: 'ADD',
                data: {},
              });
            } else {
              confirmationPopupState.setPopupValues({
                customClassName: 'no-seat-popup',
                title: 'Add User',
                content:
                  'You have used all the seats available in your current plan. To add more users, please write to us at <a href="mailto:sales@adapt.io">sales@adapt.io</a>.',
                actionBtnText: 'Ok',
                needCancelBtn: false,
                callback: () => {},
              });
              confirmationPopupState.setShowPopup(true);
            }
          }}
        >
          <PlusIcon />
          Add User
        </button>
      </div>
      <div className="table-header-wrap">
        <div className="team-table-header">
          <span>Name</span>
          <span>Email Address</span>
          <span className="credit-limit">
            Credit Limit
            <ToolTipWithIcon>
              Credit limit is the maximum credit that can be used in a month.
            </ToolTipWithIcon>
          </span>
          <span className="credit-used">
            Overall Usage
            <ToolTipWithIcon>
              This shows the total number of credits used for the billing cycle{' '}
              <span>{billingCycle}</span>
            </ToolTipWithIcon>
          </span>
          <span className="credit-used">
            Monthly Usage
            <ToolTipWithIcon>
              This shows the total number of credits between{' '}
              <span>{monthlyCycle}</span>
            </ToolTipWithIcon>
          </span>
          {/* <span className="credit-remaining">Credits Remaining</span> */}
          <span className="status">Status</span>
          <span />
        </div>
      </div>
      <div className="team-table-body">
        {profileState.isGetTeamMemberLoading
          ? Array.from({ length: 10 }).map((item, index) => {
              return (
                <div
                  key={`member-placeholder-${index}`}
                  className="team-member-row placeholder"
                >
                  <div className="name-wrapper">
                    <div className="name linear-background" />
                    <div className="last-login linear-background" />
                  </div>
                  <div className="email linear-background" />
                  <div className="credit-limit linear-background" />
                  <div className="credit-used linear-background" />
                  <div className="credit-used linear-background" />
                  {/* <div className="credit-remaining linear-background" /> */}
                  <div className="status linear-background" />
                  <div className="linear-background" />
                </div>
              );
            })
          : profileState.teamMembers.map((teamMember, index) => {
              const { status = '', emailId, memberId } = teamMember;

              const name = `${teamMember.firstName}${
                teamMember.lastName ? ` ${teamMember.lastName}` : ''
              }`;

              return (
                <div className="team-member-row" key={`${memberId}-${index}`}>
                  <div className="name-wrapper">
                    <div className="name-with-badge">
                      <span className="name" title={name}>
                        {name}
                      </span>
                      {teamMember.role === 'ADMIN' && (
                        <span className="badge">Admin</span>
                      )}
                    </div>
                    {Utils.isNotUndefinedNotNull(teamMember.lastActivity) && (
                      <div className="last-login">
                        Last Activity:{' '}
                        {Utils.getFormattedDate(teamMember.lastActivity)}
                      </div>
                    )}
                  </div>
                  <div className="email" title={emailId}>
                    {emailId}
                  </div>
                  <div className="credit-limit">
                    {status === 'INACTIVE'
                      ? ' - '
                      : teamMember.lbCreditsIndividualLimit === -1 ||
                        teamMember.lbCreditsIndividualLimit === undefined ||
                        teamMember.lbCreditsIndividualLimit === null
                      ? 'No Limit Set'
                      : Utils.numberFormat(teamMember.lbCreditsIndividualLimit)}
                  </div>
                  <div className="credit-used">
                    {teamMember.lbCreditsUsedInBillingCycle > 0
                      ? Utils.numberFormat(
                          teamMember.lbCreditsUsedInBillingCycle,
                        )
                      : '-'}
                  </div>
                  <div className="credit-used">
                    {teamMember.lbCreditsUsedMonthly > 0
                      ? Utils.numberFormat(teamMember.lbCreditsUsedMonthly)
                      : '-'}
                  </div>
                  {/* <div className="credit-remaining">
                      {teamMember.remainingIndividualLbCredits}
                    </div> */}
                  <div className={`status ${status}`}>
                    {status.toLowerCase()}
                  </div>
                  {status !== 'INACTIVE' && (
                    <Dropdown
                      toggleElement={<TripleDotNavIcon className="menu-icon" />}
                      align="end"
                      dropdownHeight={80}
                      showOnHover
                    >
                      <div className="menu-wrapper">
                        <span
                          onClick={() =>
                            setUserDataPopup({
                              show: true,
                              type: 'EDIT',
                              data: {
                                memberId: teamMember.memberId,
                                firstName: teamMember.firstName,
                                lastName: teamMember.lastName,
                                email: teamMember.emailId,
                                credits:
                                  teamMember.lbCreditsIndividualLimit || null,
                              },
                            })
                          }
                        >
                          Edit
                        </span>
                        {teamMember.role !== 'ADMIN' && (
                          <span onClick={() => removeUser(teamMember)}>
                            Delete
                          </span>
                        )}
                      </div>
                    </Dropdown>
                  )}
                </div>
              );
            })}
      </div>
      <div className="footer-text">
        {profileState.teamDetails.startDate &&
          profileState.teamDetails.endDate && (
            <span className="billing-cycle">
              Billing Cycle: <span className="text">{billingCycle}</span>
            </span>
          )}
        {profileState.teamDetails.creditResetDate && (
          <div className="note-wrapper">
            <span className="label">Note: </span>
            <span className="text">Credit limit resets automatically on </span>
            <span className="date">
              {Utils.getFormattedDate(profileState.teamDetails.creditResetDate)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

export default ManageTeam;
